import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import ChatAssistantMessageBubble from 'llm/chat-library/messages/ChatAssistantMessageBubble';
import {ChatProgressMessageBubble} from 'llm/chat-library/messages/ChatProgress';
import ChatUserMessageBubble from 'llm/chat-library/messages/ChatUserMessageBubble';
import {ClientChatEvent, ClientDisplayChatEvent} from 'llm/chat-library/messages/MessageTypes';
import {filterDisplayEvents} from 'llm/chat-library/messages/MessageUtils';
import {ChatEmptyState} from 'llm/chat-library/sidepanel/ChatEmptyState';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {ProgressData} from 'llm/deep-research/messages/DeepResearchProgress';
import {assertNever} from 'util/Types';
export interface ChatMessageThreadProps {
  events: Array<ClientChatEvent>;
  chatSessionId: string;
  onUpdateLatestUserMessageInfo?: ((info: LatestUserMessageInfo) => void) | null;
  actionButton: React.ReactNode;
  readonly: boolean;
  /**
   * Summary of tool calls used in the chat.
   *
   * This is currently only used by deep research, and if present,
   * certain UIs may render that mention "Deep research", which
   * need to be generalized by using agentType.
   *
   * TODO: Make required to support agent switching.
   */
  progressData: ProgressData | null;
}

function getEventKey(event: ClientDisplayChatEvent, idx: number): string {
  switch (event.__typename) {
    case 'ClientStreamingAssistantMessage': {
      return `streaming_${event.responseToEventId}`;
    }
    case 'ClientOptimisticUserMessage': {
      return `optimistic_${idx}`;
    }
    case 'ClientProgressMessage': {
      return `progress_${idx}`;
    }
    default:
      return event.id;
  }
}

/** @internal exported for testing */
export const _testingPerfMetrics = {
  onRender: (): void => {},
};

/**
 * Display for the current chat conversation.
 */
export const ChatMessageThread = React.memo(
  ({
    events,
    chatSessionId,
    onUpdateLatestUserMessageInfo,
    actionButton,
    readonly,
    progressData,
  }: ChatMessageThreadProps): JSX.Element => {
    _testingPerfMetrics.onRender();
    const displayEvents = filterDisplayEvents(events);

    const lastUserMessageIndex = displayEvents.findLastIndex((event) => event.role === LLMRole.USER);
    const updateLatestUserMessageElement = useCallback(
      (element: HTMLDivElement) => {
        if (onUpdateLatestUserMessageInfo) {
          onUpdateLatestUserMessageInfo({index: lastUserMessageIndex, element});
        }
      },
      [lastUserMessageIndex, onUpdateLatestUserMessageInfo]
    );

    return (
      <div className={css(styles.threadContainer)}>
        {displayEvents.length === 0 && <ChatEmptyState />}
        {displayEvents.map((event, index) => {
          if (event.__typename === 'ClientProgressMessage') {
            return <ChatProgressMessageBubble key={getEventKey(event, index)} message={event} />;
          }
          switch (event.role) {
            case LLMRole.USER:
              return (
                <ChatUserMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  chatSessionId={chatSessionId}
                  ref={index === lastUserMessageIndex ? updateLatestUserMessageElement : null}
                />
              );
            case LLMRole.ASSISTANT:
              return (
                <ChatAssistantMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  readonly={readonly}
                  progressData={progressData ?? undefined}
                  chatSessionId={chatSessionId}
                />
              );
            default:
              assertNever(event);
              // In case we introduce a new LLMRole on the backend, we fallback to the same rendering as an
              // assistant message.
              return (
                <ChatAssistantMessageBubble
                  key={getEventKey(event, index)}
                  event={event}
                  readonly={readonly}
                  progressData={progressData ?? undefined}
                  chatSessionId={chatSessionId}
                />
              );
          }
        })}
        {actionButton}
      </div>
    );
  }
);

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    width: '100%',
    padding: '16px',
    gap: '16px',
  },
});
